import { Button, Grid, List, ListItem, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { get as _get } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPlnar, createService } from '../../../api/createServiceApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import {
  getAuthKey,
  getClaimNumber,
  getClaimPlnarInfo,
  getPartnerId,
  getServicingOpCo,
  getSessionId,
  getUrlKey,
  getUserData,
  getZipCode,
} from '../../../app/store/combinedSelectors';
import * as ContextRouterActions from '../../../app/store/ContextRouter/ContextRouterActions';
import {
  updateSelectedProvidersState,
  updateStageState,
} from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { useActions } from '../../../app/store/useActions';
import {
  backBtn,
  continueBtn,
  DOCUMENT_DAMAGE_PROVIDER_NAME,
  goToClaimsBtn,
  sendLinkBtn,
} from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import getCopyById from '../../../copy/copyMapper';
import {
  CreatePlnarProjectPayload,
  CreateServicePayload,
  CreateServiceResponse,
  OpCo,
  PartnerId,
  ProviderInfo,
  StageNames,
} from '../../../types/type';
import { getNextView, getSourceSystem, getStagesInOrder, postMessageToParentApp } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import PhoneInput from '../../shared/PhoneInput/PhoneInput';
import PropertyServiceInfo from '../../shared/PropertyServiceInfo/PropertyServiceInfo';
import ServiceError from '../../shared/ServiceErrorScreen/ServiceError';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

export default function DocumentDamageServiceSelection() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [phone, setPhone] = React.useState('');
  const [isPhoneValid, setIsPhoneValid] = React.useState(false);
  const [serviceError, setServiceError] = React.useState(false);
  const [plnarSetup, setPlnarSetup] = React.useState(false);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const headerContent = 'Document your damage with Plnar Snap';
  const claimNumber = useSelector(getClaimNumber);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  let zipCode = useSelector(getZipCode);
  // If the zip code is not present in validate call response, fallback to the one which is in claim details response
  if (!zipCode) {
    zipCode = claimSummary?.lossLocationAddress.postalCode;
  }
  const userData = useSelector(getUserData);
  const plnarInfo = useSelector(getClaimPlnarInfo);
  const opco = useSelector(getServicingOpCo);
  const contextRouterActions: typeof ContextRouterActions = useActions(ContextRouterActions);

  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const currentStage = availableProgressStages.filter((stage) => stage.viewActive === true);
  const partnerId = useSelector(getPartnerId);
  const copy = getCopyById(opco, partnerId);
  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) => stage.id !== currentStage[0].id && !stage.completed && stage.isEligible && !stage.skipped
    )
  )[0];

  async function handleNext() {
    // Once success, update the local store
    updateLocalStore();
    // Go to the next screen
    gotoNextScreen();
  }

  async function setupPlnar() {
    // Show LoadingIndicator
    setModalLoadingOpen(true);
    const postBody: CreatePlnarProjectPayload = {
      claimNumber: claimNumber,
      reporter: {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.emailAddress,
        mobileNumber: '+1' + phone,
      },
      address: _get(plnarInfo, 'address'),
      sourceSystem: getSourceSystem(opco),
      lossCause: _get(plnarInfo, 'lossCause', ''),
    };
    const response: any = await createPlnar(urlKey, sessionId, authKey, postBody);

    if (response) {
      contextRouterActions.updateAuthToken(response.response.updatedAuthToken);

      // Make web service call to create a service
      const ccResponse = await updateServiceSelection(response.response.updatedAuthToken);
      // Hide LoadingIndicator
      setModalLoadingOpen(false);
      if (ccResponse) {
        // Once success, update the local store
        setPlnarSetup(true);
      } else {
        //Show UI for service error handling
        setServiceError(true);
      }
      setModalLoadingOpen(false);
    } else {
      setServiceError(true);
    }
  }

  async function updateServiceSelection(authToken: string) {
    // Show LoadingIndicator
    setModalLoadingOpen(true);
    const postBody: CreateServicePayload = {
      claimNumber: claimNumber,
      zipCode: zipCode,
      plnar: true,
    };
    const response: CreateServiceResponse = await createService(urlKey, sessionId, authToken, postBody);
    if ((_get(response, 'response.status.code', 0) as number) === 200) {
      if (_get(response, 'response.updatedAuthToken')) {
        contextRouterActions.updateAuthToken(response.response.updatedAuthToken);
      }
    }

    return response;
  }

  function updateLocalStore() {
    // Update provider's information in local store too, since the remote update was success.
    const selectedProvider: ProviderInfo = {
      serviceTypeName: StageNames.DOCUMENT_DAMAGE,
      serviceAvailable: '',
      providerName: DOCUMENT_DAMAGE_PROVIDER_NAME,
      providerID: '',
      providerPhone: '',
      providerUrl: undefined,
      subServiceTypes: undefined,
    };
    dispatch(
      updateSelectedProvidersState({
        selectedProvider: selectedProvider,
      })
    );
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === currentStage[0].id
          ? {
              ...stage,
              completed: true,
              viewActive: false,
              showProgress: true,
              skipped: false,
            }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: true,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
  }

  function gotoNextScreen() {
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (opco === OpCo.HOMESITE || opco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      navigate('/claim-detail', {
        state: {
          isClaimDetailRefreshNeeded: true,
        },
      });
    }
  }

  function phoneChange(event: any) {
    setPhone(event);
  }

  function phoneValid(event: any) {
    setIsPhoneValid(event);
  }

  function handleBack() {
    navigate(-1);
  }

  let content = <div></div>;
  if (!serviceError) {
    content = (
      <Box sx={{ width: '100%' }}>
        <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
        <PropertyServiceInfo
          headerContent={headerContent}
          infoContent={
            <>
              <Grid item xs={12}>
                <Typography sx={{ marginTop: '20px' }} variant="body1">
                  Plnar will send a download link directly to your mobile device.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ marginTop: '20px' }} variant="body1">
                  What is the best phone number to text the link to?
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    ...theme.custom.styles.formLabelStyle,
                    marginTop: '20px',
                  }}
                  display="block"
                  gutterBottom
                >
                  Your mobile phone number
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <PhoneInput hideSubCase={true} phoneValid={phoneValid} phoneValueUpdate={phoneChange} />
              </Grid>
              <Grid item xs={9}></Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    ...theme.custom.styles.disclaimerFont,
                    marginTop: '20px',
                  }}
                >
                  By providing your mobile phone number you are opting in to receive a one time text message with your
                  Plnar Snap download link. All terms and conditions, messaging, and data rates may apply. You will also
                  receive email communications from Plnar.
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '20px' }}>
                <Button
                  onClick={setupPlnar}
                  disabled={!isPhoneValid}
                  sx={{
                    ...theme.custom.styles.ctaNextButton,
                    '&.MuiButtonBase-root:hover': {
                      bgcolor: theme.custom.colors.primaryButtonHoverColor,
                    },
                  }}
                >
                  {sendLinkBtn}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    ...theme.custom.styles.serviceConfirmationHeader,
                    marginTop: '0.9rem',
                  }}
                >
                  Next Steps
                </Typography>
              </Grid>

              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <List sx={theme.custom.styles.confirmationList}>
                  <ListItem
                    sx={{
                      ...theme.custom.styles.confirmationListElement,
                      listStyleType: 'none',
                      marginLeft: '-1.5rem',
                    }}
                  >
                    <Typography variant="body1">
                      <b>1. Download Plnar Snap</b>
                    </Typography>
                  </ListItem>
                  <ListItem
                    sx={{
                      ...theme.custom.styles.confirmationListElement,
                      listStyleType: 'none',
                      marginLeft: '-1.5rem',
                    }}
                  >
                    <Typography variant="body1">
                      <b>
                        2. Open Plnar Snap on your mobile device and follow the prompts to document the damage to your
                        property.
                      </b>
                    </Typography>
                  </ListItem>
                </List>
              </Grid>
            </>
          }
        ></PropertyServiceInfo>
        {modalLoadingOpen && <ModalLinearActivityIndicator />}
        <Grid
          container
          sx={{
            width: '100%',
            maxWidth: '1280px',
            minWidth: '310px',
            margin: '0 auto',
            marginTop: '5em',
          }}
        >
          <Grid item xs={12}>
            <CallToAction
              nextButton={true}
              nextButtonContent={continueBtn}
              nextButtonDisabled={!plnarSetup}
              handleNext={handleNext}
              skipButton={false}
              backButton={true}
              backButtonContent={backBtn}
              handleBack={handleBack}
              cancelButton={true}
              cancelButtonContent={goToClaimsBtn}
              handleCancel={goToClaimsOverview}
            ></CallToAction>
          </Grid>
        </Grid>
      </Box>
    );
  } else {
    content = (
      <Box sx={{ width: '100%' }}>
        {/* Show Progress bar only if there is no service error */}
        {!serviceError && <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>}
        {serviceError && <ServiceError />}
      </Box>
    );
  }
  return <Box sx={{ width: '100%' }}>{content}</Box>;
}
